import { PropsWithChildren } from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled, { css, keyframes } from 'styled-components';

import { tokens } from '../theme/tokens';
import { Skeleton } from './Skeleton';

export const chatSlideIn = keyframes`
	0% {
		opacity: 0;
		transform: translateY(10px);
	}
	100% {
		opacity: 1;
		transform: none;
	}
`;

const slideIn = css`
	animation: 0.35s cubic-bezier(0, 1, 0, 1) ${chatSlideIn};
	animation-fill-mode: backwards;
`;

const Container = styled(Box)<{ $side: 'left' | 'right' }>`
	background: ${tokens.surface.secondary};
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${(props) =>
		props.$side === 'left'
			? `0 ${tokens.radius.md}px ${tokens.radius.md}px ${tokens.radius.md}px`
			: `${tokens.radius.md}px 0 ${tokens.radius.md}px ${tokens.radius.md}px`};
	padding: ${tokens.padding.container}px;
	flex-grow: 1;
	box-sizing: border-box;

	forced-color-adjust: exact;
	print-color-adjust: exact;
	-webkit-print-color-adjust: exact;

	@media print {
		margin-bottom: 0 !important;
	}
`;

export function Chat({
	children,
	side = 'left',
	...rest
}: PropsWithChildren<BoxProps & { side?: 'left' | 'right' }>) {
	return (
		<Container $side={side} {...rest}>
			{children}
		</Container>
	);
}

const BubbleContainer = styled(Box)<{ $type: 'ai' | 'user' }>`
	display: flex;
	flex-direction: column;
	color: ${(props) =>
		props.color || (props.$type === 'ai' ? tokens.brand.text : tokens.text.primary)};
	background: ${(props) =>
		props.$type === 'ai' ? tokens.brand.background : tokens.surface.secondary};
	border-radius: ${tokens.radius.md}px;
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
	${slideIn}
	padding: ${tokens.padding.container}px;
	word-break: break-word;

	hr {
		background-color: ${tokens.border.subtle};
	}
`;

function ChatBubble({
	type = 'ai',
	children,
	...rest
}: PropsWithChildren<BoxProps & { type?: 'ai' | 'user' }>) {
	return (
		<BubbleContainer $type={type} {...rest}>
			{children}
		</BubbleContainer>
	);
}

const TypingContainer = styled(Flex)`
	background: ${tokens.surface.secondary};
	border-radius: ${tokens.radius.sm}px;
	align-items: center;
	gap: ${tokens.gap.inline}px;
	padding: ${tokens.padding.container}px;
	line-height: ${tokens.typography.lineHeights.body};
	${slideIn}
	animation-delay: 300ms;
`;

function ChatTyping(props: BoxProps) {
	return (
		<TypingContainer {...props}>
			<Skeleton.Box width={6} height={6} style={{ borderRadius: tokens.radius.xs }} />
			<Skeleton.Box width={6} height={6} style={{ borderRadius: tokens.radius.xs }} />
			<Skeleton.Box width={6} height={6} style={{ borderRadius: tokens.radius.xs }} />
		</TypingContainer>
	);
}

const AvatarSlot = styled(Box)`
	${slideIn}
	flex-shrink: 0;
`;

function ChatAvatar(props: PropsWithChildren<BoxProps>) {
	return <AvatarSlot {...props} />;
}

Chat.Bubble = ChatBubble;
Chat.Typing = ChatTyping;
Chat.Avatar = ChatAvatar;
